import {
  FormikInputField,
  FormikSelectField,
  useCountryList,
} from '@/features/common';
import { useWorkspacePermission } from '@/features/permissions';
import { useWorkspaceId } from '@/hooks/router';
import { getCustomerQueryKey } from '@/queries/subscriptions/keys';
import {
  usePostCustomerMutation,
  usePutCustomerMutation,
} from '@/queries/subscriptions/mutations';
import { useGetCustomer } from '@/queries/subscriptions/queries';
import { SubscriptionsMutateCustomerRequest } from '@/queries/types';
import { queryClient } from '@/utils/reactQuery';
import styled from '@emotion/styled';
import { DSButton } from '@hundred5/design-system';
import { Country } from 'country-list';
import { Form, Formik, FormikHelpers } from 'formik';
import React from 'react';
import { object, string } from 'yup';
import { useHandleBillingError } from '../../hooks';

interface BillingDetailsCustomerEditFormProps {
  onSuccess?: () => void;
}

const getValidationSchema = (countries: Country[]) =>
  object().shape({
    name: string().required("This can't be blank!"),
    email: string()
      .required("This can't be blank!")
      .email('Enter a valid email'),
    countryCode: string()
      .required("This can't be blank!")
      .oneOf(
        countries.map((c) => c.code),
        'Select a valid country'
      ),
    address: string().required("This can't be blank!"),
  });

export function BillingDetailsCustomerEditForm({
  onSuccess,
}: BillingDetailsCustomerEditFormProps) {
  const workspaceId = useWorkspaceId();
  const { handleUnknownError } = useHandleBillingError();
  const { countries } = useCountryList();
  const { data: customer } = useGetCustomer(Number(workspaceId));
  const createCustomerMutation = usePostCustomerMutation();
  const updateCustomerMutation = usePutCustomerMutation();
  const validationSchema = getValidationSchema(countries);
  const canEditBillingInfo = useWorkspacePermission()('billing info', 'update');

  const handleSubmit = (
    values: SubscriptionsMutateCustomerRequest,
    formikHelpers: FormikHelpers<SubscriptionsMutateCustomerRequest>
  ) => {
    if (!customer) {
      createCustomerMutation.mutate(
        {
          workspace_id: Number(workspaceId),
          data: {
            countryCode: values.countryCode,
            email: values.email,
            name: values.name,
            address: values.address ? values.address : undefined,
            postalCode: values.postalCode ? values.postalCode : undefined,
            vat: values.vat ? values.vat : undefined,
          },
        },
        {
          onSuccess: handleSuccess,
          onError: (error) => handleError(error),
          onSettled: () => handleSettled(formikHelpers),
        }
      );
    } else {
      updateCustomerMutation.mutate(
        {
          workspace_id: Number(workspaceId),
          data: {
            countryCode: values.countryCode,
            email: values.email,
            name: values.name,
            address: values.address ? values.address : undefined,
            postalCode: values.postalCode ? values.postalCode : undefined,
            vat: values.vat ? values.vat : undefined,
          },
        },
        {
          onSuccess: handleSuccess,
          onError: (error) => handleError(error),
          onSettled: () => handleSettled(formikHelpers),
        }
      );
    }
  };

  const handleSuccess = () => {
    queryClient.invalidateQueries({
      queryKey: getCustomerQueryKey(Number(workspaceId)),
    });
    onSuccess?.();
  };

  const handleError = (error: Error) => {
    handleUnknownError('Failed to update customer', error);
  };

  const handleSettled = (
    formikHelpers: FormikHelpers<SubscriptionsMutateCustomerRequest>
  ) => {
    formikHelpers.setSubmitting(false);
  };

  return (
    <Formik<SubscriptionsMutateCustomerRequest>
      initialValues={{
        name: customer?.name ?? '',
        email: customer?.email ?? '',
        vat: customer?.vat ?? '',
        countryCode: customer?.countryCode ?? '',
        postalCode: customer?.postalCode ?? '',
        address: customer?.address ?? '',
      }}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({ isValid, isSubmitting }) => (
        <Form data-recording-ignore="mask">
          <Content data-recording-ignore="mask">
            <FormikInputField label="Payer" name="name" required />
            <FormikInputField label="VAT number" name="vat" />
            <FormikInputField label="Email" name="email" required />
            <FormikSelectField
              label="Country"
              name="countryCode"
              items={countries.map((country) => ({
                id: country.code,
                label: country.name,
                labelText: country.name,
              }))}
              required
            />
            <FormikInputField label="ZIP/Postal code" name="postalCode" />
            <FormikInputField label="Address" name="address" required />
          </Content>

          <Footer>
            <DSButton
              variant="primary-purple"
              type="submit"
              disabled={!canEditBillingInfo || !isValid || isSubmitting}
              data-rh={
                !canEditBillingInfo ? 'Only owners can edit details' : null
              }
            >
              {isSubmitting ? 'Saving...' : 'Save changes'}
            </DSButton>
          </Footer>
        </Form>
      )}
    </Formik>
  );
}

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Footer = styled.footer`
  padding-top: 36px;
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  align-items: center;
`;
